import React, { useContext } from 'react';
import c from 'classnames';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import ElementQueries from '../../../ElementQueries';
import { CommentEditorContext } from '../../../CommentEditor/context';
import { setRefs } from '../../../utils/refHelpers';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';

import s from './CommentBoxRoot.scss';

export interface Props extends React.ComponentPropsWithoutRef<'div'> {
  /** Will add a `small-editor` global class to the CommentBoxRoot element so that we're using the small editor styling */
  isSmall?: boolean;
  className?: string;
}

/**
 * The top component in our CommentBox tree.
 *
 * Provides an ElementsQueries component and disables theme skinning.
 */
const CommentBoxRoot = React.forwardRef<HTMLDivElement, Props>(
  ({ children, className, isSmall }, ref) => {
    const { editorId, isExpanded, rootElementRef } =
      useContext(CommentEditorContext);
    const { isLoadingTriggered } = useContext(LoadableComponentContext);

    return (
      <ErrorBoundary debugComponent="CommentBoxRoot">
        <ElementQueries
          id={editorId}
          // Whenever the comment box is collapsed, we need a place to put focus that isn't a now-hidden element. Following
          // popup logic and dropping focus back to the containing element
          tabIndex={-1}
          className={c(
            'no-theme-skin',
            s['commentbox'],
            isExpanded && s['expanded'],
            isSmall && !(isExpanded || isLoadingTriggered) && 'small-editor',
            className
          )}
          forwardedRef={node => setRefs([ref, rootElementRef], node)}
          widthBreakpoints={[490]}
        >
          {children}
        </ElementQueries>
      </ErrorBoundary>
    );
  }
);
CommentBoxRoot.displayName = 'CommentBoxRoot';

export default CommentBoxRoot;
